// useFeatureAccess.ts
import { useCallback } from 'react';
import { useUserStore } from './useUserStore';
import {
  isFeatureEnabled,
  getFeatureLimit,
  hasFeatureAccess,
  getFeatureRemainingUsage,
  FeatureCode,
  getRecordingResolution,
} from '../utils/userUtils';
import { IUserRootResponse } from '../types/user';

export interface UseFeatureAccessResult {
  checkFeature: (featureCode: FeatureCode) => boolean;
  getLimit: (featureCode: FeatureCode) => number | null;
  hasAccess: (featureCode: FeatureCode, currentUsage?: number) => boolean;
  getRemainingUsage: (featureCode: FeatureCode, currentUsage: number) => number | null;
  getRecordingResolutionLimit: () => number | null;
  userData: IUserRootResponse | null;
  isPaid: boolean;
}

/**
 * Hook to access user feature permissions and limits
 * 
 * This hook provides a React-friendly interface to check user permissions,
 * get feature limits, and validate feature access. It integrates with the
 * user store and caches results for performance.
 * 
 * @example
 * ```tsx
 * const { checkFeature, getLimit, hasAccess } = useFeatureAccess();
 * 
 * // Check boolean feature
 * const hasStudio = checkFeature('STUDIO'); // true/false
 * 
 * // Get numeric limit
 * const durationLimit = getLimit('DURATION_PER_VIDEO'); // 300 (seconds) or null
 * 
 * // Check access with usage tracking
 * const canRecord = hasAccess('DURATION_PER_VIDEO', currentUsage); // true/false
 * ```
 * 
 * @returns Object with methods to check features and get limits
 */
export function useFeatureAccess(): UseFeatureAccessResult {
  const { userData } = useUserStore();

  // Check if a boolean feature is enabled
  const checkFeature = useCallback(
    (featureCode: FeatureCode): boolean => {
      return isFeatureEnabled(userData, featureCode);
    },
    [userData]
  );

  // Get numeric limit for a feature
  const getLimit = useCallback(
    (featureCode: FeatureCode): number | null => {
      return getFeatureLimit(userData, featureCode);
    },
    [userData]
  );

  // Check if user has access to a feature (considering usage)
  const hasAccess = useCallback(
    (featureCode: FeatureCode, currentUsage: number = 0): boolean => {
      return hasFeatureAccess(userData, featureCode, currentUsage);
    },
    [userData]
  );

  // Get remaining usage for a quota feature
  const getRemainingUsage = useCallback(
    (featureCode: FeatureCode, currentUsage: number): number | null => {
      return getFeatureRemainingUsage(userData, featureCode, currentUsage);
    },
    [userData]
  );

  // Get recording resolution limit (shortcut)
  const getRecordingResolutionLimit = useCallback((): number | null => {
    return getRecordingResolution(userData);
  }, [userData]);

  const isPaid = userData?.isPaid === true;

  return {
    checkFeature,
    getLimit,
    hasAccess,
    getRemainingUsage,
    getRecordingResolutionLimit,
    userData,
    isPaid,
  };
}
